import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, doc, updateDoc, query, where, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Search, Filter, Mail, Calendar, UserPlus, MapPin, DollarSign, X } from 'lucide-react';
const getCurrency = (country) => {
  if (!country) return 'KES';
  const c = country.toLowerCase();
  if (c.includes('uganda')) return 'UGX';
  if (c.includes('tanzania')) return 'TZS';
  if (c.includes('rwanda')) return 'RWF';
  if (c.includes('burundi')) return 'BIF';
  if (c.includes('south sudan')) return 'SSP';
  if (c.includes('nigeria')) return 'NGN';
  if (c.includes('south africa')) return 'ZAR';
  if (c.includes('united states') || c.includes('usa')) return 'USD';
  if (c.includes('united kingdom') || c.includes('uk')) return 'GBP';
  return 'KES'; // Default
};

export default function DispatchQueue() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [filterCountry, setFilterCountry] = useState('');
  const [minContribution, setMinContribution] = useState(0);

  // Slide-out assignment panel
  const [selectedApp, setSelectedApp] = useState(null);
  const [advisorEmail, setAdvisorEmail] = useState('');
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    const fetchQueue = async () => {
      setLoading(true);
      try {
        const snap = await getDocs(collection(db, 'onboarding'));
        const data = [];
        snap.forEach(d => {
          const app = { id: d.id, ...d.data() };
          // Only show those needing interviews
          if (app.sponsorship === 'pay_slowly' || app.sponsorship === 'nef' || app.sponsorship === 'unsure') {
            data.push(app);
          }
        });
        setApplications(data.sort((a, b) => new Date(b.updatedAt?.seconds * 1000 || 0) - new Date(a.updatedAt?.seconds * 1000 || 0)));
      } catch (err) {
        console.error("Failed to fetch dispatch queue", err);
      }
      setLoading(false);
    };
    fetchQueue();
  }, []);

  const uniqueDates = [...new Set(applications.map(a => a.interviewDate).filter(Boolean))].sort();
  const uniqueCountries = [...new Set(applications.map(a => a.country).filter(Boolean))].sort();

  const filteredApps = useMemo(() => {
    return applications.filter(app => {
      const name = `${app.firstName || ''} ${app.lastName || ''}`.toLowerCase();
      const matchesSearch = name.includes(searchTerm.toLowerCase()) || (app.email || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesDate = !filterDate || app.interviewDate === filterDate;
      const matchesCountry = !filterCountry || app.country === filterCountry;
      
      const pCont = parseInt((app.personalContribution || '').replace(/[^0-9]/g, ''), 10) || 0;
      const matchesContribution = pCont >= minContribution;

      return matchesSearch && matchesDate && matchesCountry && matchesContribution;
    });
  }, [applications, searchTerm, filterDate, filterCountry, minContribution]);

  const handleAssign = async () => {
    if (!advisorEmail || !selectedApp) return;
    setAssigning(true);
    try {
      // 1. Update onboarding doc
      await updateDoc(doc(db, 'onboarding', selectedApp.id), {
        assignedAdvisorEmail: advisorEmail,
        appointmentStatus: 'assigned'
      });
      
      // 2. Add to admissions_appointments to lock the slot
      if (selectedApp.interviewDate && selectedApp.interviewTime) {
         // Create a unique ID or use composite ID
         const slotId = `${selectedApp.interviewDate}_${selectedApp.interviewTime.replace(/\s+/g, '')}`;
         await updateDoc(doc(db, 'admissions_appointments', slotId), {
            date: selectedApp.interviewDate,
            timeSlot: selectedApp.interviewTime,
            studentId: selectedApp.id,
            advisorEmail: advisorEmail,
            status: 'assigned',
            studentName: `${selectedApp.firstName} ${selectedApp.lastName}`,
            studentEmail: selectedApp.email
         }).catch(async (e) => {
            // If it doesn't exist, we must create it. But updateDoc fails if not exists. Use setDoc instead.
            const { setDoc } = await import('firebase/firestore');
            await setDoc(doc(db, 'admissions_appointments', slotId), {
                date: selectedApp.interviewDate,
                timeSlot: selectedApp.interviewTime,
                studentId: selectedApp.id,
                advisorEmail: advisorEmail,
                status: 'assigned',
                studentName: `${selectedApp.firstName} ${selectedApp.lastName}`,
                studentEmail: selectedApp.email
            });
         });
      }

      setApplications(prev => prev.map(a => 
        a.id === selectedApp.id ? { ...a, assignedAdvisorEmail: advisorEmail, appointmentStatus: 'assigned' } : a
      ));
      
      setSelectedApp(null);
      setAdvisorEmail('');
      alert("Successfully assigned and locked slot. Google Meet logic will fire via Cloud Function.");
    } catch (e) {
      console.error(e);
      alert("Failed to assign");
    }
    setAssigning(false);
  };

  return (
    <div className="space-y-6 relative h-full">
      <header>
        <h1 className="text-3xl font-black uppercase tracking-tighter text-black">Dispatch Queue</h1>
        <p className="text-xs font-bold text-zinc-500 uppercase tracking-widest mt-2">Manage Financial Interview Assignments</p>
      </header>

      {/* Advanced Filter Engine */}
      <div className="bg-white border border-zinc-200 p-6 rounded-2xl shadow-sm">
        <h2 className="text-[10px] font-black uppercase tracking-widest text-[#8B1A2D] mb-4 flex items-center gap-2">
          <Filter size={14} /> Multi-Variate Filters
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-2">Search Name/Email</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={16} />
              <input 
                type="text" 
                className="w-full pl-9 pr-4 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-sm focus:outline-none focus:border-black transition-colors"
                placeholder="Search..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-2">Interview Date</label>
            <select 
              className="w-full px-4 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-sm focus:outline-none focus:border-black"
              value={filterDate}
              onChange={e => setFilterDate(e.target.value)}
            >
              <option value="">All Dates</option>
              {uniqueDates.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-2">Geography</label>
            <select 
              className="w-full px-4 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-sm focus:outline-none focus:border-black"
              value={filterCountry}
              onChange={e => setFilterCountry(e.target.value)}
            >
              <option value="">All Countries</option>
              {uniqueCountries.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-2">Min. Contribution (KES)</label>
            <div className="flex items-center gap-3">
              <input 
                type="range" 
                min="0" max="5000000" step="5000"
                className="w-full accent-black cursor-pointer"
                value={minContribution}
                onChange={e => setMinContribution(parseInt(e.target.value))}
              />
              <span className="text-xs font-bold text-black">{minContribution.toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Queue List */}
      <div className="bg-white border border-zinc-200 rounded-2xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-zinc-400 font-bold uppercase tracking-widest text-xs">Loading queue...</div>
        ) : filteredApps.length === 0 ? (
          <div className="p-12 text-center text-zinc-400 font-bold uppercase tracking-widest text-xs">No pending requests found</div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {filteredApps.map(app => (
              <div key={app.id} className="p-6 hover:bg-zinc-50 transition-colors flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-black text-black">{app.firstName} {app.lastName}</h3>
                  <div className="flex items-center gap-4 mt-2">
                    <span className="text-xs font-bold text-zinc-500 flex items-center gap-1"><Calendar size={12}/> {app.interviewDate || 'Not selected'} @ {app.interviewTime || 'N/A'}</span>
                    <span className="text-xs font-bold text-zinc-500 flex items-center gap-1"><MapPin size={12}/> {app.county}, {app.country}</span>
                    <span className="text-xs font-bold text-green-600 flex items-center gap-2">
                      <span className="text-[9px] font-black uppercase tracking-widest bg-green-100 text-green-700 px-1.5 py-0.5 rounded">{getCurrency(app.country)}</span>
                      {app.personalContribution || 'Unknown'}
                    </span>
                  </div>
                </div>
                <button 
                  onClick={() => setSelectedApp(app)}
                  className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-colors ${
                    app.appointmentStatus === 'assigned' 
                      ? 'bg-zinc-100 text-zinc-400 cursor-not-allowed' 
                      : 'bg-[#8B1A2D] text-white hover:bg-black'
                  }`}
                  disabled={app.appointmentStatus === 'assigned'}
                >
                  {app.appointmentStatus === 'assigned' ? 'Assigned' : 'Delegate'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Slide-out Panel */}
      <div className={`fixed inset-y-0 right-0 w-[400px] bg-white border-l border-zinc-200 shadow-2xl transform transition-transform duration-300 ease-in-out z-50 flex flex-col ${selectedApp ? 'translate-x-0' : 'translate-x-full'}`}>
        {selectedApp && (
          <>
            <div className="p-6 border-b border-zinc-100 flex items-center justify-between bg-zinc-50">
              <div>
                <h2 className="text-lg font-black tracking-tighter uppercase text-black">Assign Delegate</h2>
                <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mt-1">For {selectedApp.firstName} {selectedApp.lastName}</p>
              </div>
              <button onClick={() => setSelectedApp(null)} className="p-2 text-zinc-400 hover:text-black transition-colors rounded-lg hover:bg-zinc-200">
                <X size={20} />
              </button>
            </div>
            
            <div className="p-6 flex-1 overflow-y-auto">
              <div className="bg-[#8B1A2D]/5 border border-[#8B1A2D]/10 rounded-xl p-4 mb-6">
                <div className="text-[10px] font-black text-[#8B1A2D] uppercase tracking-widest mb-2">Requested Time</div>
                <div className="text-lg font-black text-black">{selectedApp.interviewDate}</div>
                <div className="text-sm font-bold text-zinc-600">{selectedApp.interviewTime}</div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-2">Advisor Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={16} />
                    <input 
                      type="email" 
                      className="w-full pl-9 pr-4 py-3 bg-zinc-50 border border-zinc-200 rounded-xl text-sm font-bold focus:outline-none focus:border-black transition-colors"
                      placeholder="advisor@dynamicindutechno.solutions"
                      value={advisorEmail}
                      onChange={e => setAdvisorEmail(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-zinc-100 bg-zinc-50">
              <button 
                onClick={handleAssign}
                disabled={assigning || !advisorEmail}
                className="w-full py-4 bg-black text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-[#8B1A2D] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {assigning ? 'Assigning...' : <><UserPlus size={16} /> Assign & Generate Meet</>}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Overlay */}
      {selectedApp && (
        <div 
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40"
          onClick={() => setSelectedApp(null)}
        />
      )}
    </div>
  );
}
